import React from "react";
import { Price } from "@/components/ui/price";
import { formatDate, formatDateTime, formatNumber } from "@/lib/utils";

export type CellValue = string | number | null | undefined;

/** One column of a report table, shared by the on-screen table and the Excel export. */
export interface ReportColumn<Row> {
	key: string;
	/** Already-translated header. */
	label: string;
	/** Raw value used for sorting, search, totals and export. Money/numbers must be numbers. */
	value: (row: Row) => CellValue;
	/** How the value is shown and exported (default "text"). */
	kind?: "text" | "money" | "number" | "percent" | "date" | "datetime";
	/** Custom cell content (e.g. a status badge); `value` still drives sort/search/export. */
	render?: (row: Row) => React.ReactNode;
	/** Sum this column in the totals row. */
	total?: boolean;
	align?: "start" | "center" | "end";
	/** Secondary column hidden below the md breakpoint (still exported). */
	hideOnMobile?: boolean;
	/** Currency of money columns (defaults to SAR inside <Price>). */
	currency?: string;
}

export function isNumericColumn<Row>(col: ReportColumn<Row>): boolean {
	return col.kind === "money" || col.kind === "number" || col.kind === "percent";
}

/** Value as plain text, as the user reads it (search matches this). */
export function cellText<Row>(col: ReportColumn<Row>, row: Row): string {
	return formatValue(col, col.value(row));
}

export function formatValue<Row>(col: ReportColumn<Row>, value: CellValue): string {
	if (value === null || value === undefined || value === "") return "-";
	switch (col.kind) {
		case "money":
		case "number":
			return formatNumber(roundTo(Number(value) || 0, 2));
		case "percent":
			return formatPercent(Number(value) || 0);
		case "date":
			return formatDate(value);
		case "datetime":
			return formatDateTime(value);
		default:
			return String(value);
	}
}

/** Cell content: the column's own renderer, else the value formatted by kind. */
export function ReportValue<Row>({
	column,
	row,
	value,
}: {
	column: ReportColumn<Row>;
	row?: Row;
	/** Explicit value (totals row); otherwise read from `row`. */
	value?: CellValue;
}) {
	if (row !== undefined && column.render) return <>{column.render(row)}</>;
	const raw = row !== undefined ? column.value(row) : value;
	if (column.kind === "money" && raw !== null && raw !== undefined && raw !== "") {
		return <Price value={roundTo(Number(raw) || 0, 2)} currency={column.currency} />;
	}
	const text = formatValue(column, raw);
	return isNumericColumn(column) || column.kind === "date" || column.kind === "datetime" ? (
		<span dir="ltr" className="tabular-nums">
			{text}
		</span>
	) : (
		<>{text}</>
	);
}

export function roundTo(value: number, digits: number): number {
	const f = 10 ** digits;
	return Math.round(value * f) / f;
}

/**
 * "12.5%", wrapped in a left-to-right isolate (U+2066…U+2069) so it keeps its order inside Arabic
 * sentences ("الهامش 12.5%" would otherwise render the sign on the wrong side).
 */
export function formatPercent(value: number): string {
	return `⁦${formatNumber(roundTo(value, 1))}%⁩`;
}

/** Number from an API value that may arrive as a decimal string (entity money columns do). */
export function num(value: unknown): number {
	const n = typeof value === "number" ? value : parseFloat(String(value ?? ""));
	return Number.isFinite(n) ? n : 0;
}

/** Share of `part` in `whole` as a percentage (0 when the whole is 0). */
export function share(part: number, whole: number): number {
	return whole ? (part / whole) * 100 : 0;
}

/** "First Last" from an entity with name parts, else its fullName, else "-". */
export function personName(
	person?: { firstName?: string | null; lastName?: string | null; fullName?: string | null } | null,
) {
	if (!person) return "";
	return [person.firstName, person.lastName].filter(Boolean).join(" ").trim() || person.fullName?.trim() || "";
}
